/** Keep loaded cards in their own panel when switching filters or visiting a detail. */
export function bindProjectPaging(group, prepareBatch) {
  const ends = [...group.querySelectorAll('.project-page-end')];
  const pending = new WeakMap();
  let observer;

  function load(panel) {
    if (!panel.dataset.next || panel.hidden) return;
    if (!pending.has(panel)) {
      pending.set(panel, appendBatch(panel).finally(() => pending.delete(panel)));
    }
    return pending.get(panel);
  }

  async function appendBatch(panel) {
    const grid = panel.querySelector('.projects-grid');
    const end = panel.querySelector('.project-page-end');
    const status = panel.querySelector('.project-page-status');
    grid.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(panel.dataset.next);
      if (!response.ok) throw new Error('Project page unavailable');
      const page = await response.json();
      const template = document.createElement('template');
      template.innerHTML = page.html;
      prepareBatch(template.content);
      grid.append(template.content);
      panel.dataset.next = page.next;
      end.replaceChildren();
      end.setAttribute('aria-hidden', 'true');
      status.textContent = '';
      if (!page.next) {
        observer?.unobserve(end);
        end.remove();
      }
    } catch {
      status.textContent = 'Ďalšie projekty sa nepodarilo načítať.';
      if (!end.childElementCount) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Skúsiť znova';
        retry.addEventListener('click', () => load(panel));
        end.removeAttribute('aria-hidden');
        end.append(retry);
      }
    } finally {
      grid.removeAttribute('aria-busy');
    }
  }

  if (ends.length && typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) load(entry.target.parentElement);
      }
    }, { rootMargin: '0px 0px 180px' });
    ends.forEach(end => observer.observe(end));
  } else if (ends.length) {
    const check = () => {
      const end = group.querySelector('.project-content.active .project-page-end');
      if (end?.getClientRects().length && end.getBoundingClientRect().top <= window.innerHeight + 180)
        load(end.parentElement);
    };
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
  }

  return {
    openPanel(panel) {
      if (panel.dataset?.next && !panel.querySelector('.projects-grid').childElementCount) load(panel);
    },
    async restoreCount(panel, count, stillCurrent) {
      const grid = panel.querySelector('.projects-grid');
      while (stillCurrent() && grid.childElementCount < count) {
        const before = grid.childElementCount;
        await load(panel);
        // The catalogue may have changed, or the request may need a manual retry.
        if (grid.childElementCount === before) return false;
      }
      return stillCurrent();
    },
  };
}
